/* eslint-disable react-hooks/exhaustive-deps */

'use client';

import { ChartOptions } from 'chart.js';
import clsx from 'clsx';
import Image from 'next/image';

import { useLocale, useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';
import { Checkbox } from '../../Checkbox/Checkbox';
import Counter from '../../Counter/Counter';
import { Icon } from '../../Icon/Icon';
import { Modal } from '../../Modal/Modal';
import ResponsiveChart from '../../ResponsiveChart/ResponsiveChart';
import StatsCard from '../../StatsCard';
import { SimulationSection } from '../SimulationSection/SimulationSection';

import { LocalizedPrice } from '@/components/storyblok-components/LocalizedPrice/LocalizedPrice';
import { Accordion } from '@/components/ui/Accordion/Accordion';
import { localizePriceCurrency } from '@/utils/storyblok';
import 'swiper/css';
import 'swiper/css/pagination';
import styles from './ConfiguratorSimulation.module.scss';

type SectionProps = {
    results: any[];
    className?: string;
    investmentAmount: number;
    exchangeRate: number;
    currency: string;
    counterValue?: number;
    setCounterValue?: (el: any) => void;
};

export const ConfiguratorSimulation = ({
    results,
    className,
    investmentAmount,
    exchangeRate,
    currency,
    counterValue = 5,
    setCounterValue,
}: SectionProps) => {
    const g = useTranslations('Common');
    const t = useTranslations('ConfiguratorSimulation');
    const [activeId, setActiveId] = useState(null);
    const [showAllLocations, setShowAllLocations] = useState(false);
    const locale = useLocale();
    const selectedLocation = useMemo(() => {
        if (!activeId) return null;
        return (
            Object.values(results)
                .flat()
                .map((item: any) => item.location)
                .find((loc: any) => loc.name === activeId) || null
        );
    }, [activeId, results]);

    const { displayResults, hasHiddenLocations } = useMemo(() => {
        const grouped: any = {};
        let hidden = false;

        Object.entries(results).forEach(([city, locs]) => {
            const visibleLocs = (locs as any[]).filter(({ location }) => {
                const allBelowInvestment = location.types.every((type: any) => {
                    const end = parseFloat(type.endingPriceUsd as any) || 0;
                    return end < investmentAmount;
                });

                if (allBelowInvestment) {
                    if (!showAllLocations) {
                        hidden = true;
                        return false;
                    }
                    return true;
                }

                return true;
            });

            if (visibleLocs.length) {
                grouped[city] = visibleLocs;
            }
        });

        return { displayResults: grouped, hasHiddenLocations: hidden };
    }, [results, investmentAmount, showAllLocations]);

    // const appreciationRate = Number((selectedLocation?.appreciation ?? '').split('-').pop()?.trim()) || 0;
    const appreciationRate = selectedLocation?.appreciation ? parseFloat(selectedLocation?.appreciation || 0) : 0;

    const statsData = [
        { label: g('longTermRoi'), value: selectedLocation?.longTermRoi },
        { label: g('shortTermRoi'), value: selectedLocation?.shortTermRoi },
        { label: g('appreciation'), value: selectedLocation?.appreciation },
    ].filter(({ value }) => value && parseFloat(value) !== 0);

    const [selectedRentType, setSelectedRentType] = useState('shortTerm');

    const totalAppreciation = useMemo(
        () =>
            counterValue > 0 ? investmentAmount * ((1 + (appreciationRate || 0) / 100) ** (counterValue - 1) - 1) : 0,
        [counterValue, investmentAmount, appreciationRate],
    );

    const totalRentingRoi = useMemo(() => {
        if (!selectedRentType) return 0;
        const rentRate =
            selectedRentType === 'shortTerm'
                ? parseFloat(selectedLocation?.shortTermRoi)
                : parseFloat(selectedLocation?.longTermRoi);
        return counterValue > 0 ? investmentAmount * ((1 + rentRate / 100) ** (counterValue - 1) - 1) : 0;
    }, [
        counterValue,
        investmentAmount,
        selectedRentType,
        selectedLocation?.shortTermRoi,
        selectedLocation?.longTermRoi,
    ]);
    const customOptions: ChartOptions<'bar'> = {
        plugins: {
            title: {
                text: t('chart.title'),
            },
        },
    };

    const dynamicChartData = useMemo(() => {
        const currentYear = new Date().getFullYear();
        let labels: string[] = [];
        labels = Array.from({ length: counterValue }, (_, i) => String(currentYear + i));

        const datasets = [
            {
                label: t('chart.investment'),
                data: labels.map(() => investmentAmount),
                backgroundColor: 'rgba(173, 0, 38, 0.5)',
            },
            {
                label: t('chart.appreciation'),
                data: labels.map((_, i) => {
                    if (i === 0) return 0;
                    return investmentAmount * ((1 + (appreciationRate || 0) / 100) ** i - 1);
                }),
                backgroundColor: 'rgba(36, 97, 0, 0.5)',
            },
        ];

        if (selectedRentType) {
            const rentRate =
                selectedRentType === 'shortTerm'
                    ? parseFloat(selectedLocation?.shortTermRoi)
                    : parseFloat(selectedLocation?.longTermRoi);
            datasets.push({
                label: t('chart.rent'),
                data: labels.map((_, i) => {
                    if (i === 0) return 0;
                    return investmentAmount * ((1 + rentRate / 100) ** i - 1);
                }),
                backgroundColor: 'rgba(255, 204, 0, 0.5)',
            });
        }

        return { labels, datasets };
    }, [
        counterValue,
        selectedRentType,
        investmentAmount,
        appreciationRate,
        selectedLocation?.longTermRoi,
        selectedLocation?.shortTermRoi,
    ]);

    return (
        <>
            <div className={clsx(styles.root, className)}>
                <h6 className={styles.amountRow}>
                    {t('amountToInvest')}{' '}
                    <span
                        style={{
                            background: 'black',
                            color: 'white',
                            fontWeight: '900',
                            padding: '4px',
                        }}
                    >
                        {
                            localizePriceCurrency(investmentAmount, 'USD', currency, locale, {
                                exchangeRate,
                            }).converted
                        }
                    </span>
                </h6>
                <div>
                    {Object.entries(displayResults).map(([city, locs], index) => (
                        <Accordion
                            key={city}
                            title={city}
                            className={styles.accordion}
                            isOpen
                            icon={
                                <div
                                    style={{
                                        padding: '6px 10px',
                                        border: '1.5px solid black',
                                        borderRadius: '22px',
                                    }}
                                >
                                    {index + 1}
                                </div>
                            }
                        >
                            <div className={styles.innerWrap}>
                                {(locs as any[]).map(({ location }) => (
                                    <div
                                        className={styles.locCard}
                                        key={location.name}
                                    >
                                        {location?.cover?.filename && (
                                            <>
                                                <div className={styles.layer} />
                                                <Image
                                                    className={styles.cover}
                                                    src={location.cover.filename}
                                                    alt={location.cover.alt}
                                                    fill
                                                    objectFit="cover"
                                                />
                                            </>
                                        )}
                                        <SimulationSection
                                            location={location}
                                            investmentAmount={investmentAmount}
                                            exchangeRate={exchangeRate}
                                            currency={currency}
                                        />
                                        <div
                                            onClick={() => setActiveId(location.name)}
                                            className={styles.actions}
                                        >
                                            <div>{t('view')}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Accordion>
                    ))}
                    {!showAllLocations && hasHiddenLocations && (
                        <div style={{ textAlign: 'center', marginTop: '24px' }}>
                            <button
                                onClick={() => setShowAllLocations(true)}
                                className={styles.loadMoreButton}
                            >
                                {t('amountToInvest')}
                            </button>
                        </div>
                    )}
                </div>
            </div>
            <Modal
                isOpen={!!selectedLocation}
                onClose={() => setActiveId(null)}
                maxWidth="900px"
                className={styles.modal}
            >
                <div>
                    <h2>{t('modal.titleSelected', { location: selectedLocation?.name })}</h2>

                    <h4 className={styles.amountRow}>
                        {t('amountToInvest')}{' '}
                        <span
                            style={{
                                background: 'black',
                                color: 'white',
                                fontWeight: '900',
                                padding: '4px',
                            }}
                        >
                            {
                                localizePriceCurrency(investmentAmount, 'USD', currency, locale, {
                                    exchangeRate,
                                }).converted
                            }
                        </span>
                    </h4>

                    <div>
                        {statsData.length > 0 && (
                            <div
                                style={{
                                    marginTop: '16px',
                                }}
                            >
                                <h4>{t('modal.expectedReturn')}</h4>
                                <div
                                    style={{
                                        maxWidth: 'fit-content',
                                        marginTop: '8px',
                                    }}
                                >
                                    <StatsCard stats={statsData} />
                                </div>
                            </div>
                        )}
                    </div>

                    <div className={styles.middleWrap}>
                        <div className={styles.counterSection}>
                            <h4>{t('modal.howManyYears')}</h4>

                            <div className={styles.counterWrap}>
                                <h3>{t('modal.yearsLabel')}</h3>
                                <div className={styles.wrp}>
                                    <Counter
                                        value={counterValue}
                                        places={[100, 10, 1]}
                                        fontSize={80}
                                        padding={5}
                                        gap={10}
                                        textColor="white"
                                        fontWeight={900}
                                    />
                                    <div
                                        className={styles.counterAction}
                                        style={{ marginTop: '10px' }}
                                    >
                                        <Icon
                                            className={clsx(styles.counterActionIcon, {
                                                [styles.disabled]: counterValue <= 5,
                                            })}
                                            onClick={() =>
                                                counterValue <= 5 ? undefined : setCounterValue(prev => prev - 1)
                                            }
                                            style={{ marginRight: '5px', fontSize: '22px' }}
                                            name="minus"
                                        />
                                        <Icon
                                            className={clsx(styles.counterActionIcon, {
                                                [styles.disabled]: counterValue >= 50,
                                            })}
                                            onClick={() =>
                                                counterValue >= 50 ? undefined : setCounterValue(prev => prev + 1)
                                            }
                                            style={{ marginRight: '5px', fontSize: '22px' }}
                                            name="plus"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {(selectedLocation?.longTermRoi || selectedLocation?.shortTermRoi) && (
                            <div>
                                <h4>{t('modal.planToRent')}</h4>
                                {selectedLocation?.longTermRoi && (
                                    <div
                                        style={{
                                            marginTop: '12px ',
                                        }}
                                    >
                                        <Checkbox
                                            label={t('modal.longTermRent')}
                                            onChange={() =>
                                                setSelectedRentType(selectedRentType === 'longTerm' ? null : 'longTerm')
                                            }
                                            checked={selectedRentType === 'longTerm'}
                                        />
                                    </div>
                                )}

                                {selectedLocation?.shortTermRoi && (
                                    <div
                                        style={{
                                            marginTop: '12px ',
                                        }}
                                    >
                                        <Checkbox
                                            label={t('modal.shortTermRent')}
                                            onChange={() =>
                                                setSelectedRentType(
                                                    selectedRentType === 'shortTerm' ? null : 'shortTerm',
                                                )
                                            }
                                            checked={selectedRentType === 'shortTerm'}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div className={styles.simulationSection}>
                        <div className={styles.expectedProfits}>
                            <h4>{t('modal.expectedProfits')}</h4>
                            <div
                                style={{
                                    minHeight: '300px',
                                }}
                            >
                                <ResponsiveChart
                                    data={dynamicChartData}
                                    options={customOptions}
                                />
                            </div>

                            <h4
                                style={{
                                    marginTop: '36px',
                                }}
                            >
                                {t('modal.overallGain', { years: counterValue })}
                            </h4>
                            <div>
                                <div
                                    style={{
                                        marginTop: '12px',
                                    }}
                                >
                                    <LocalizedPrice
                                        className={styles.localized}
                                        price={totalAppreciation}
                                    />{' '}
                                    {t('modal.appreciationComment')}
                                </div>
                                <div
                                    style={{
                                        marginTop: '12px',
                                    }}
                                >
                                    <LocalizedPrice
                                        className={styles.localized}
                                        price={totalRentingRoi}
                                    />{' '}
                                    {t('modal.rentGainComment')}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};
